import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["dropzone", "prompt", "filesList", "selectedFilesContainer", "selectedFilesList", "fileCount", "bulkDescription", "submitButton", "cameraModal", "video", "canvas", "captureButton", "retakeButton", "confirmButton", "statusContainer", "statusDisplay", "statusSpinner", "statusMessage", "statusDetail", "overallProgress", "overallProgressBar"]

  connect() {
    this.selectedFiles = []
    this.stream = null
    this.uploadProgresses = new Map()
    this.uploadingState = false
    this.setupDragAndDrop()
  }

  setupDragAndDrop() {
    const dropzone = this.dropzoneTarget

    dropzone.addEventListener('dragover', (e) => {
      e.preventDefault()
      dropzone.classList.add('border-blue-500', 'bg-blue-50')
    })

    dropzone.addEventListener('dragleave', (e) => {
      e.preventDefault()
      dropzone.classList.remove('border-blue-500', 'bg-blue-50')
    })

    dropzone.addEventListener('drop', (e) => {
      e.preventDefault()
      dropzone.classList.remove('border-blue-500', 'bg-blue-50')

      const files = Array.from(e.dataTransfer.files)
      this.addFiles(files)
    })
  }

  selectFiles() {
    // Create a temporary file input
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    input.accept = 'image/jpeg,image/png,image/gif,image/webp'
    input.style.display = 'none'
    input.style.position = 'absolute'
    input.style.left = '-9999px'

    input.addEventListener('change', (event) => {
      const files = Array.from(event.target.files)
      this.addFiles(files)
      // Remove the temporary input
      input.remove()
    })

    // Append to body and click
    document.body.appendChild(input)
    input.click()
  }


  addFiles(files) {
    // Validate files
    const validFiles = files.filter(file => {
      const maxSize = 50 * 1024 * 1024 // 50MB
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 50MB.`)
        return false
      }

      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} has unsupported format. Please use JPEG, PNG, GIF, or WebP images.`)
        return false
      }

      return true
    })

    // Generate previews for image files
    validFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        this.generateImagePreview(file)
      }
    })

    // Add to selected files
    this.selectedFiles = [...this.selectedFiles, ...validFiles]
    this.updateSelectedFiles()
    this.updateSubmitButton()
  }

  updateFilesList() {
    if (this.selectedFiles.length === 0) {
      this.filesListTarget.classList.add('hidden')
      this.promptTarget.classList.remove('hidden')
      return
    }

    this.promptTarget.classList.add('hidden')
    this.filesListTarget.classList.remove('hidden')

    this.filesListTarget.innerHTML = this.selectedFiles.map((file, index) => {
      const progress = this.uploadProgresses.get(file) || 0
      const isUploading = this.uploadingState && this.uploadProgresses.has(file) && this.uploadProgresses.get(file) < 100
      const isCompleted = progress === 100

      return `
        <div class="flex items-center justify-between p-3 bg-gray-50 rounded-md" data-file-index="${index}">
          <div class="flex items-center space-x-3 flex-1">
            <div class="flex-shrink-0">
              ${this.getFileIcon(file.type)}
            </div>
            <div class="flex-1">
              <p class="text-sm font-medium text-gray-900">${file.name}</p>
              <p class="text-xs text-gray-500">${this.formatFileSize(file.size)}</p>
              ${isUploading || isCompleted ? this.renderProgressBar(file, progress, isCompleted) : ''}
            </div>
          </div>
          ${!isUploading ? `
            <button type="button"
                    class="text-red-600 hover:text-red-800"
                    data-action="click->file-upload#removeFile"
                    data-file-index="${index}">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
              </svg>
            </button>
          ` : ''}
        </div>
      `
    }).join('')

    // Update selected files container
    this.updateSelectedFiles()
  }

  renderProgressBar(file, progress, isCompleted) {
    const progressClass = isCompleted ? 'bg-green-500' : 'bg-blue-500'
    const statusText = isCompleted ? 'Upload complete' : `Uploading... ${Math.round(progress)}%`

    return `
      <div class="mt-2">
        <div class="flex items-center justify-between text-xs text-gray-600">
          <span>${statusText}</span>
          <span>${Math.round(progress)}%</span>
        </div>
        <div class="mt-1 w-full bg-gray-200 rounded-full h-2">
          <div class="h-2 rounded-full transition-all duration-300 ${progressClass}"
               style="width: ${progress}%"></div>
        </div>
      </div>
    `
  }

  updateSelectedFiles() {
    if (this.selectedFiles.length === 0) {
      if (this.hasSelectedFilesContainerTarget) {
        this.selectedFilesContainerTarget.classList.add('hidden')
      }
      return
    }

    if (this.hasSelectedFilesContainerTarget) {
      this.selectedFilesContainerTarget.classList.remove('hidden')

      // Update file count
      if (this.hasFileCountTarget) {
        this.fileCountTarget.textContent = this.selectedFiles.length
      }

      // Update individual files list with previews and descriptions
      if (this.hasSelectedFilesListTarget) {
        this.selectedFilesListTarget.innerHTML = this.selectedFiles.map((file, index) => {
          const progress = this.uploadProgresses.get(file) || 0
          const isUploading = this.uploadingState && this.uploadProgresses.has(file) && this.uploadProgresses.get(file) < 100
          const isCompleted = progress === 100
          const previewUrl = file.previewUrl || ''

          return `
            <div class="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-file-index="${index}">
              <div class="flex items-start justify-between">
                <div class="flex items-start space-x-3 flex-1">
                  <div class="flex-shrink-0">
                    ${previewUrl ? `
                      <img src="${previewUrl}" alt="Preview of ${file.name}" class="w-16 h-16 object-cover rounded-md border">
                    ` : `
                      <div class="w-16 h-16 bg-gray-100 rounded-md border flex items-center justify-center">
                        ${this.getFileIcon(file.type)}
                      </div>
                    `}
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 truncate">${file.name}</p>
                    <p class="text-xs text-gray-500">${this.formatFileSize(file.size)}</p>
                    ${isUploading || isCompleted ? this.renderProgressBar(file, progress, isCompleted) : ''}
                  </div>
                </div>
                ${!isUploading ? `
                  <button type="button"
                          class="text-red-600 hover:text-red-800 ml-3"
                          data-action="click->file-upload#removeFile"
                          data-file-index="${index}">
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                    </svg>
                  </button>
                ` : ''}
              </div>

              <!-- Description input for this file -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Description for this file
                </label>
                <textarea name="upload_session[descriptions][]"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          rows="2"
                          data-file-index="${index}"
                          placeholder="Add a description for this file (optional)"></textarea>
              </div>
            </div>
          `
        }).join('')
      }
    }
  }

  updateSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = this.selectedFiles.length === 0
    }
  }

  removeFile(event) {
    const index = parseInt(event.target.closest('[data-file-index]').dataset.fileIndex)
    const file = this.selectedFiles[index]

    // Revoke preview URL to prevent memory leaks
    if (file.previewUrl) {
      URL.revokeObjectURL(file.previewUrl)
    }

    this.selectedFiles.splice(index, 1)
    this.updateFilesList()
    this.updateSelectedFiles()
    this.updateSubmitButton()
  }

  getFileIcon(mimeType) {
    if (mimeType.startsWith('image/')) {
      return `<svg class="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"></path>
      </svg>`
    } else if (mimeType === 'application/pdf') {
      return `<svg class="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"></path>
      </svg>`
    } else {
      return `<svg class="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"></path>
      </svg>`
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Camera capture functionality
  async openCamera() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera by default
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      })

      this.videoTarget.srcObject = this.stream
      this.cameraModalTarget.classList.remove('hidden')
      this.videoTarget.play()

      // Show capture button, hide others
      this.captureButtonTarget.classList.remove('hidden')
      this.retakeButtonTarget.classList.add('hidden')
      this.confirmButtonTarget.classList.add('hidden')
    } catch (error) {
      console.error('Error accessing camera:', error)
      alert('Unable to access camera. Please check permissions and try again.')
    }
  }

  capturePhoto() {
    const video = this.videoTarget
    const canvas = this.canvasTarget
    const context = canvas.getContext('2d')

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Hide capture button, show retake and confirm
    this.captureButtonTarget.classList.add('hidden')
    this.retakeButtonTarget.classList.remove('hidden')
    this.confirmButtonTarget.classList.remove('hidden')

    // Stop video stream temporarily
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
    }
  }

  retakePhoto() {
    // Show capture button, hide others
    this.captureButtonTarget.classList.remove('hidden')
    this.retakeButtonTarget.classList.add('hidden')
    this.confirmButtonTarget.classList.add('hidden')

    // Restart camera
    this.openCamera()
  }

  confirmPhoto() {
    const canvas = this.canvasTarget

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      // Create a file-like object from the blob
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      const file = new File([blob], `camera-capture-${timestamp}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      })

      // Add the captured file to selected files
      this.addFiles([file])

      // Close camera modal
      this.closeCamera()
    }, 'image/jpeg', 0.85)
  }

  closeCamera() {
    // Stop video stream
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }

    // Hide modal
    this.cameraModalTarget.classList.add('hidden')

    // Reset button states
    this.captureButtonTarget.classList.remove('hidden')
    this.retakeButtonTarget.classList.add('hidden')
    this.confirmButtonTarget.classList.add('hidden')
  }

  // ActiveStorage DirectUpload functionality
  async startDirectUpload(file, description) {
    return new Promise((resolve, reject) => {
      // Create a delegate object to handle progress tracking
      const delegate = {
        directUploadWillStoreFileWithXHR: (request) => {
          // Store the xhr for potential cleanup
          if (!this.activeUploads) {
            this.activeUploads = new Map()
          }
          this.activeUploads.set(file, request)

          // Add progress tracking to the xhr request
          request.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progress = (event.loaded / event.total) * 100
              this.uploadProgresses.set(file, progress)
              this.updateFilesList()

              // Update overall progress if we're in uploading state
              if (this.uploadingState) {
                this.calculateOverallProgress()
              }
            }
          })
        }
      }

      const upload = new DirectUpload(file, '/rails/active_storage/direct_uploads', delegate)

      this.uploadProgresses.set(file, 0)
      this.updateFilesList()

      upload.create((error, blob) => {
        // Cleanup the active upload reference
        if (this.activeUploads) {
          this.activeUploads.delete(file)
        }

        if (error) {
          console.error(`Direct upload failed for ${file.name}:`, error)
          this.uploadProgresses.delete(file)
          this.updateFilesList()
          reject(error)
        } else {
          this.uploadProgresses.set(file, 100)
          this.updateFilesList()
          resolve({ blob, description })
        }
      })
    })
  }

  calculateOverallProgress() {
    if (this.selectedFiles.length === 0) return

    const totalProgress = Array.from(this.uploadProgresses.values()).reduce((sum, progress) => sum + progress, 0)
    const averageProgress = totalProgress / this.selectedFiles.length

    this.updateOverallProgress(averageProgress)
  }

  // Enhanced form submission with ActiveStorage DirectUpload
  async submitForm(event) {
    event.preventDefault()

    if (this.selectedFiles.length === 0) {
      alert("Please select at least one file to upload.")
      return
    }

    try {
      this.uploadingState = true
      this.showUploadStatus()
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = "Uploading..."

      // Update status
      this.updateStatusMessage("Uploading files to secure storage...", "Please wait while your files are uploaded.")

      // Get descriptions for each file
      const descriptions = this.getFileDescriptions()

      // Upload all files using ActiveStorage DirectUpload
      const uploads = await Promise.all(this.selectedFiles.map((file, index) =>
        this.startDirectUpload(file, descriptions[index])
      ))

      // Create upload session with all the uploaded blobs
      const result = await this.createUploadSession(uploads)

      // Update status for completion
      this.updateStatusMessage("Upload completed successfully!", "All files have been uploaded and processed.")
      this.updateOverallProgress(100)
      this.showUploadSuccess("Files uploaded successfully!")

      // Redirect to the upload session page after a brief delay
      setTimeout(() => {
        window.location.href = result.redirect_url || '/uploads'
      }, 2000)

    } catch (error) {
      console.error("Upload failed:", error)
      this.showUploadError("Upload failed. Please try again.")
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = "Upload Files"
      this.uploadingState = false
    }
  }

  async createUploadSession(uploads) {
    const formData = new FormData(this.element)

    // Add the signed blob IDs from DirectUpload
    uploads.forEach((upload, index) => {
      formData.append(`uploads[${index}][blob_id]`, upload.blob.signed_id)
      formData.append(`uploads[${index}][description]`, upload.description || '')
    })

    const response = await fetch('/uploads', {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to create upload session')
    }

    return await response.json()
  }

  getFileDescriptions() {
    // Get descriptions from the individual file description inputs
    const descriptionInputs = this.selectedFilesListTarget.querySelectorAll('textarea[name="upload_session[descriptions][]"]')
    return Array.from(descriptionInputs).map(input => input.value || '')
  }

  // Status display methods
  showUploadStatus() {
    this.statusContainerTarget.classList.remove('hidden')
    this.statusSpinnerTarget.classList.remove('hidden')
  }

  hideUploadStatus() {
    this.statusContainerTarget.classList.add('hidden')
  }

  updateStatusMessage(message, detail = "") {
    this.statusMessageTarget.textContent = message
    this.statusDetailTarget.textContent = detail
  }

  updateOverallProgress(percentage) {
    const roundedPercentage = Math.round(percentage)
    this.overallProgressTarget.textContent = `${roundedPercentage}%`
    this.overallProgressBarTarget.style.width = `${roundedPercentage}%`
  }

  showUploadSuccess(message) {
    this.statusDisplayTarget.className = "bg-green-50 border border-green-200 rounded-lg p-4"
    this.statusSpinnerTarget.classList.add('hidden')
    this.updateStatusMessage("Upload completed successfully!", message)
  }

  showUploadError(message) {
    this.statusDisplayTarget.className = "bg-red-50 border border-red-200 rounded-lg p-4"
    this.statusSpinnerTarget.classList.add('hidden')
    this.updateStatusMessage("Upload failed", message)
  }

  addSignedIdInputs() {
    // Remove any existing signed_id inputs
    this.element.querySelectorAll('input[name*="signed_id"]').forEach(input => input.remove())

    // Add signed_id inputs for each uploaded file
    this.selectedFiles.forEach((file, index) => {
      if (file.signed_id) {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = `upload_session[signed_ids][]`
        input.value = file.signed_id
        this.element.appendChild(input)
      }
    })
  }

  // New methods for file description management
  applyDescriptionToAll() {
    const bulkDescription = this.bulkDescriptionTarget.value.trim()
    if (!bulkDescription) {
      alert('Please enter a description to apply to all files.')
      return
    }

    // Find all description textareas and update them
    const descriptionTextareas = this.selectedFilesListTarget.querySelectorAll('textarea[name="upload_session[descriptions][]"]')
    descriptionTextareas.forEach(textarea => {
      textarea.value = bulkDescription
    })

    // Clear the bulk description input
    this.bulkDescriptionTarget.value = ''
  }

  clearAllFiles() {
    if (this.selectedFiles.length === 0) return

    if (confirm('Are you sure you want to clear all selected files?')) {
      // Revoke all preview URLs to prevent memory leaks
      this.selectedFiles.forEach(file => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl)
        }
      })

      this.selectedFiles = []
      this.updateFilesList()
      this.updateSelectedFiles()
      this.updateSubmitButton()
    }
  }

  generateImagePreview(file) {
    if (!file.type.startsWith('image/')) return

    const reader = new FileReader()
    reader.onload = (e) => {
      file.previewUrl = e.target.result
      // Re-render the selected files to show the preview
      this.updateSelectedFiles()
    }
    reader.readAsDataURL(file)
  }

  disconnect() {
    // Clean up camera stream if component is disconnected
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
    }

    // Cancel any ongoing uploads
    if (this.activeUploads) {
      this.activeUploads.forEach(xhr => {
        if (xhr && xhr.abort) {
          xhr.abort()
        }
      })
      this.activeUploads.clear()
    }

    // Clean up preview URLs to prevent memory leaks
    this.selectedFiles.forEach(file => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl)
      }
    })
  }
}